/**
 * Tirer pour rafraîchir (Today) : le geste, sans React ni DOM réel, pour qu'un test le joue.
 *
 * Un doigt posé quand la page est **déjà en haut**, qui descend franchement (plus qu'il ne
 * glisse de côté), tire l'indicateur ; relâché au-delà de `SEUIL`, il déclenche `onRefresh`,
 * en-deçà il ne fait rien. Tant que le geste tire, `touchmove` est annulé : ni rebond du
 * navigateur ni son propre rechargement de page (Chrome hors PWA) ne passent dessous.
 */

/** Distance affichée (px, après amorti) au-delà de laquelle relâcher rafraîchit. */
export const SEUIL = 64;
/** L'indicateur ne descend pas plus bas, quel que soit le geste. */
export const MAX = 96;
/** Ce que le doigt doit parcourir avant qu'on décide si le geste est vertical. */
const AMORCE = 8;

/** Le doigt parcourt deux fois ce que l'indicateur descend : la résistance d'un élastique. */
export function amorti(dy: number): number {
  return dy <= 0 ? 0 : Math.min(MAX, dy / 2);
}

interface Point {
  clientX: number;
  clientY: number;
}

/** Le strict nécessaire d'un `TouchEvent`, pour qu'un test le fabrique. */
export interface Toucher {
  touches: ArrayLike<Point>;
  cancelable: boolean;
  preventDefault(): void;
}

export interface Options {
  /** La page est-elle tout en haut ? (`window.scrollY <= 0`) */
  enHaut(): boolean;
  /** Un geste qui ne doit pas tirer : une modale ouverte, par exemple. */
  bloque(): boolean;
  /** La distance (px) à afficher, 0 au repos. */
  surTirage(distance: number): void;
  onRefresh(): Promise<void>;
}

/** Branche le geste sur `cible` (la fenêtre) ; rend de quoi le débrancher. */
export function brancherTirage(cible: EventTarget, o: Options): () => void {
  let depart: Point | null = null;
  // null : pas encore décidé ; true : le geste tire ; false : il défile ou glisse, on le laisse.
  let tire: boolean | null = null;
  let distance = 0;
  let enCours = false;

  const repos = (): void => {
    depart = null;
    tire = null;
    if (distance !== 0) o.surTirage((distance = 0));
  };

  const onStart = (e: Event): void => {
    const t = e as unknown as Toucher;
    if (enCours || t.touches.length !== 1 || !o.enHaut() || o.bloque()) return;
    const p = t.touches[0]!;
    depart = { clientX: p.clientX, clientY: p.clientY };
    tire = null;
  };

  const onMove = (e: Event): void => {
    const t = e as unknown as Toucher;
    if (!depart || tire === false) return;
    if (t.touches.length !== 1) return repos();
    const p = t.touches[0]!;
    const dx = p.clientX - depart.clientX;
    const dy = p.clientY - depart.clientY;
    if (tire === null) {
      if (Math.abs(dx) < AMORCE && Math.abs(dy) < AMORCE) return;
      tire = dy > 0 && Math.abs(dy) > Math.abs(dx) && o.enHaut();
      if (!tire) return;
    }
    if (t.cancelable) t.preventDefault();
    const d = amorti(dy);
    if (d !== distance) o.surTirage((distance = d));
  };

  const onEnd = (): void => {
    const declenche = tire === true && distance >= SEUIL;
    repos();
    if (!declenche) return;
    enCours = true;
    void o.onRefresh().finally(() => {
      enCours = false;
    });
  };

  // `passive: false` sur touchmove : sans lui, `preventDefault` est ignoré et le navigateur
  // fait défiler (ou recharge) la page sous l'indicateur.
  cible.addEventListener('touchstart', onStart, { passive: true });
  cible.addEventListener('touchmove', onMove, { passive: false });
  cible.addEventListener('touchend', onEnd);
  cible.addEventListener('touchcancel', repos);
  return () => {
    cible.removeEventListener('touchstart', onStart);
    cible.removeEventListener('touchmove', onMove);
    cible.removeEventListener('touchend', onEnd);
    cible.removeEventListener('touchcancel', repos);
  };
}
